// ScrollLock.jsx
// Reference: https://github.com/theKashey/react-remove-scroll

import React from 'react';
import PropTypes from 'prop-types';

import {
  aggressive,
  getTouchY,
  getShouldCancelScroll,
} from '../resource/scrollUtils.js';

class ScrollLock extends React.PureComponent {
  constructor(props) {
    super(props);
    this.documentEventMap = {
      wheel: this.handleDocumentScroll,
      touchmove: this.handleDocumentScroll,
      touchstart: this.handleDocumentTouchStart,
    };
  }

  static lockElementList = new Set();
  childRef = React.createRef();
  shouldPreventQueue = [];
  touchStartY = 0;

  attachDocumentEvent = () => {
    for (const eventName in this.documentEventMap) {
      document.addEventListener(
        eventName,
        this.documentEventMap[eventName],
        aggressive
      );
    }
  };
  detachDocumentEvent = () => {
    for (const eventName in this.documentEventMap) {
      document.removeEventListener(
        eventName,
        this.documentEventMap[eventName],
        aggressive
      );
    }
  };

  componentDidMount() {
    ScrollLock.lockElementList.add(this.childRef.current);
    this.attachDocumentEvent();
  }

  componentWillUnmount() {
    ScrollLock.lockElementList.delete(this.childRef.current);
    this.detachDocumentEvent();
  }

  /**@param {TouchEvent} event*/
  handleDocumentTouchStart = e => {
    this.touchStartY = getTouchY(e);
  };

  /**@param {TouchEvent} event*/
  getIsTargetInScrollLock = e => !!this.getAncestorWithScrollLock(e.target);

  /**@param {node} node*/
  getAncestorWithScrollLock = node => {
    if (node === document.body) return null;
    if (ScrollLock.lockElementList.has(node)) return node;
    return this.getAncestorWithScrollLock(node.parentNode);
  };

  /**@param {WheelEvent|TouchEvent} e*/
  handleDocumentScroll = e => {
    const delta = e.deltaY || getTouchY(e);
    const sourceEvent = this.shouldPreventQueue.filter(
      event =>
        event.name === e.type &&
        event.delta === delta &&
        event.target === e.target
    )[0];
    // self event, and should be canceled
    if (sourceEvent && sourceEvent.should && event.cancelable) {
      e.preventDefault();
      return;
    }
    // outside
    if (!sourceEvent && event.cancelable && !this.getIsTargetInScrollLock(e)) {
      e.preventDefault();
    }
  };

  getDeltaY = e => {
    switch (e.type) {
      case 'wheel':
      case 'scroll':
        return e.deltaY;
      case 'touchmove':
        return this.touchStartY - getTouchY(e);
    }
    throw new Error(`Unexpected event type: ${e.type}`);
  };

  handleChildScrollCapture = e => {
    const deltaY = this.getDeltaY(e);
    const item = {
      name: e.type,
      delta: e.type === 'wheel' ? deltaY : getTouchY(e),
      target: e.target,
      should: getShouldCancelScroll(this.childRef.current, e, deltaY),
    };
    this.shouldPreventQueue.push(item);
    setTimeout(() => {
      this.shouldPreventQueue = this.shouldPreventQueue.filter(e => e !== item);
    }, 1);
  };

  render() {
    const { children } = this.props;
    const style = {
      WebkitOverflowScrolling: 'touch',
    };

    const props = {
      ref: this.childRef,
      onScrollCapture: this.handleChildScrollCapture,
      onWheelCapture: this.handleChildScrollCapture,
      onTouchMoveCapture: this.handleChildScrollCapture,
    };

    return (
      <div style={style} {...props} data-scroll_lock>
        {children}
      </div>
    );
  }
}

ScrollLock.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ScrollLock;
